// Study sync's own state on this device, revault:sync (a device key: it never leaves this browser):
//   { sub, rev, hash, dirty, lastPush, lastPull, paused, off, ask, resets, seen, offset, kept }
//   sub     the account this browser syncs for (a hash of it, not the id itself)
//   rev     the account copy's revision this browser last merged
//   hash    the data that copy held, so a push only happens when something really changed
//   dirty   a change here that hasn't reached the account yet
//   paused  "too-big" | "busy" | "error" | "full" | "off" | null: why sync is waiting
//   off     the account this browser stopped syncing for ("Not now" or "Stop syncing")
//   ask     the account waiting on "Add them to your account?"
//   resets  { key: ts } when "Clear study stats" last ran for this account, so other devices clear
//           what they held from before it too (the merge rule is in shared/merge-save.js)
//   seen    { key: ts } the newest of those this browser's own stats already reflect. Kept for as
//           long as this browser syncs for the account, so a reset clears things here only once;
//           a device joining the account starts from the account's resets and clears nothing
//   offset  how far this browser's clock is ahead of the server's (ms), when it's off by more than a
//           few seconds, so a reset is recorded in the server's time
//   kept    { subHash: { seen, resets, at } } for the last few accounts that signed out here, so
//           signing back in (and "Add them") doesn't bring back stats cleared on another device
//           in the meantime. Only reset times (maybe none), never any study data.
// Small on purpose: Settings and the stats reset read it without loading the sync chunk.
import { readJSON, save, remove } from "./store.js";

export const SYNC_KEY = "revault:sync";
const isObj = (v) => !!v && typeof v === "object" && !Array.isArray(v);

export function readSync() { const v = readJSON(SYNC_KEY, null); return isObj(v) ? v : {}; }
export function writeSync(patch) { return save(SYNC_KEY, Object.assign(readSync(), patch)); }
// Signing out: the sync state goes, except the account's reset times (see kept above). Deleting
// the account passes { keep: false } and forgets everything.
export const KEPT_MAX = 4;
export function clearSync({ keep = true } = {}) {
	const st = readSync();
	const kept = keep && isObj(st.kept) ? { ...st.kept } : {};
	const has = (m) => isObj(m) && Object.keys(m).length > 0;
	// Even with no resets yet: that this browser synced for the account is what matters. Stats it
	// still holds were in the account before any reset made while it was signed out.
	if (keep && st.sub) kept[st.sub] = { seen: has(st.seen) ? st.seen : {}, resets: has(st.resets) ? st.resets : {}, at: Date.now() };
	const ids = Object.keys(kept).sort((a, b) => (Number(kept[b] && kept[b].at) || 0) - (Number(kept[a] && kept[a].at) || 0)).slice(0, KEPT_MAX);
	if (!ids.length) { remove(SYNC_KEY); return; }
	const next = { kept: Object.fromEntries(ids.map((id) => [id, kept[id]])) };
	if (Number.isFinite(st.offset) && st.offset) next.offset = st.offset;
	save(SYNC_KEY, next);
}

// A short one-way hash of an account id: enough to tell two accounts apart on one device.
export function subHash(sub) {
	let a = 0x811c9dc5, b = 0x9e3779b9;
	const s = String(sub || "");
	for (let i = 0; i < s.length; i++) {
		const ch = s.charCodeAt(i);
		a = Math.imul(a ^ ch, 0x01000193) >>> 0;
		b = Math.imul(b ^ ch, 0x5bd1e995) >>> 0;
	}
	return a.toString(36) + b.toString(36);
}

// Syncing right now for the account `me` (a subHash)?
export function syncingFor(me, st = readSync()) { return !!me && st.sub === me && st.off !== me && st.ask !== me; }

// "Clear study stats" with sync on: remember when, so the account copy and the other devices
// clear the same stats instead of the larger number winning them straight back.
export function noteReset(keys, now = Date.now()) {
	const st = readSync();
	if (!st.sub || st.off === st.sub) return false;
	const resets = Object.assign({}, st.resets), seen = Object.assign({}, st.seen);
	// In the server's time, as near as this browser knows it, so two devices whose clocks disagree
	// still put their resets in the order they happened. And always later than any reset this
	// browser already knows of: a reset made after another must never look older than it.
	const at = now - (Number.isFinite(st.offset) ? st.offset : 0);
	for (const k of keys) { const t = Math.max(at, Math.max(Number(resets[k]) || 0, Number(seen[k]) || 0) + 1); resets[k] = t; seen[k] = t; }
	return writeSync({ resets, seen, dirty: true });
}

// The status line in Settings > Account.
export function statusLine(st, me, now = Date.now()) {
	if (!me) return "";
	if (st.ask === me) return "This device already has study work. Add it to your account?";
	if (st.off === me) return "Sync is off on this device.";
	if (st.sub !== me) return "Getting ready to sync.";
	if (st.paused === "too-big") return "Sync paused: your study work is bigger than sync can hold (1 MB). Your work is safe on this device; a save file still works.";
	if (st.paused === "full") return "Sync paused: this device is out of space for saves.";
	if (st.paused === "off") return "Sync is resting for a bit. Your work is safe on this device.";
	if (st.paused) return "Sync paused. Your work is safe on this device.";
	const t = Math.max(Number(st.lastPush) || 0, Number(st.lastPull) || 0);
	if (!t) return "Not synced yet.";
	const min = Math.floor((now - t) / 60000);
	const when = min < 1 ? "just now" : min < 60 ? min + " min ago" : min < 1440 ? Math.floor(min / 60) + " h ago" : new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" });
	return (st.dirty ? "Some changes are waiting to sync. Last synced " : "Synced ") + when + ".";
}
